import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, FileText, Plus, ShoppingCart } from 'lucide-react';
import { BottomSheet } from '@evi/ui';
import { formatCents } from '@/lib/api';
import { useCartStore } from '@/store/cart';
import { toast } from '@/store/toast';
import { FAMILY_MANUALS, type ProductFamily } from './storeCatalog';
import { ManualCaptureSheet } from './ManualCaptureSheet';
import styles from './Store.module.css';

interface Props {
  family: ProductFamily | null;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

/**
 * Product-with-options bottom sheet. Shows the family media, its variants as
 * selectable rows, and a live price that tracks the selection. Add resolves to
 * the concrete SKU so the cart store keeps its existing per-SKU shape.
 */
export function ProductOptionsSheet({ family, onClose, onNavigate }: Props) {
  const { t } = useTranslation('store');
  const add = useCartStore((s) => s.add);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [manualOpen, setManualOpen] = useState(false);
  // Manual row keys on FAMILY_MANUALS — no entry for this family, no row.
  const manual = family ? FAMILY_MANUALS[family.key] : undefined;

  // Reset the selection to the first variant whenever a new family opens —
  // the recommended one when a family flags it, otherwise the cheapest
  // (variants are sorted recommended-first, then by price).
  useEffect(() => {
    if (family) setSelectedId(family.variants[0]?.product.id ?? null);
  }, [family]);

  const selected = family?.variants.find((v) => v.product.id === selectedId) ?? family?.variants[0] ?? null;
  const multi = (family?.variants.length ?? 0) > 1;

  const handleAdd = (): void => {
    if (!selected) return;
    add(selected.product);
    toast(t('toast.added', { name: family?.title }), { tone: 'accent' });
    onClose();
  };

  return (
    <BottomSheet isOpen={family != null} onClose={onClose} title={family?.title ?? ''} glass>
      {family && selected && (
        <div className={styles.sheet}>
          <div className={styles.sheetMedia}>
            {family.media?.film ? (
              <video
                className={styles.sheetVideo}
                src={family.media.film}
                poster={family.media.poster ?? family.media.hero}
                autoPlay
                muted
                loop
                playsInline
              />
            ) : family.media ? (
              <img className={styles.sheetImg} src={family.media.hero} alt={family.title} />
            ) : (
              <div className={styles.sheetImgFallback} aria-hidden />
            )}
          </div>

          {/* Selected variant's full description (headline + detail set) with
              the family blurb as fallback, so the detail text renders on the
              sheet the same way it does on the detail page. */}
          {(selected.product.description ?? family.blurb) && (
            <p className={styles.sheetBlurb}>{selected.product.description ?? family.blurb}</p>
          )}

          {/* Download the manual — same capture flow as the detail page,
              directly under the family blurb where buyers read the copy.
              Keyed on FAMILY_MANUALS so families without a PDF render no
              row. Opens the shared ManualCaptureSheet (rendered below). */}
          {manual && (
            <button
              type="button"
              className={styles.sheetViewCart}
              onClick={() => setManualOpen(true)}
            >
              <FileText size={15} /> {t('downloadManual')}
            </button>
          )}

          {multi && (
            <div className={styles.optionList} role="radiogroup" aria-label={t('options.label')}>
              {family.variants.map((v) => {
                const isSel = v.product.id === selected.product.id;
                return (
                  <button
                    key={v.product.id}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    className={`${styles.optionRow} ${isSel ? styles.optionRowSel : ''}`}
                    onClick={() => setSelectedId(v.product.id)}
                  >
                    <span className={styles.optionCheck} aria-hidden>
                      {isSel && <Check size={13} />}
                    </span>
                    <span className={styles.optionLabel}>{v.label}</span>
                    {v.product.recommended && (
                      <span className={styles.optionBadge}>{t('options.recommended')}</span>
                    )}
                    <span className={styles.optionPrice}>{formatCents(v.product.price_cents)}</span>
                  </button>
                );
              })}
            </div>
          )}

          <div className={styles.sheetPriceRow}>
            <span className={styles.sheetPriceLabel}>{t('price')}</span>
            <span className={styles.sheetPrice}>
              {formatCents(selected.product.price_cents)} <span className={styles.usd}>USD</span>
            </span>
          </div>
          {selected.product.lead_time_label && (
            <p className={styles.installNote}>{selected.product.lead_time_label}</p>
          )}

          <button type="button" className={styles.sheetAdd} onClick={handleAdd}>
            <Plus size={16} /> {t('add')}
          </button>
          <button
            type="button"
            className={styles.sheetViewCart}
            onClick={() => { onClose(); onNavigate('/store/cart'); }}
          >
            <ShoppingCart size={15} /> {t('viewCart')}
          </button>

          {/* Shared capture sheet (same component as ProductDetailPage, no
              fork). It renders its own BottomSheet and portals to
              document.body, so its position in this tree is inconsequential;
              it opens over the options sheet. */}
          {manual && (
            <ManualCaptureSheet
              isOpen={manualOpen}
              onClose={() => setManualOpen(false)}
              manual={manual}
            />
          )}
        </div>
      )}
    </BottomSheet>
  );
}
